import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import PageSection from 'components/page/page-section/PageSection';
import SortableTable from 'components/sortable-table/SortableTable';
import Pagination from 'components/pagination/Pagination';

import CcmCampaignReportingTableOutfitDetailsItems from './outfit-details-items/CcmCampaignReportingTableOutfitDetailsItems';

import { dataProvider } from 'modules/reporting/utils/onsite-performance';
import { getSortedData } from '../utils/ccm-campaign-reporting';
import {
  getPerformanceByCountryColumns,
  getPerformanceByCountryOutfits
} from '../utils/ccm-campaign-reporting-columns';

import './ccm-campaign-reporting-table.scss';

import type {
  LocationWindow,
  SortableTableSort
} from 'types';
import type {
  CcmCampaignReportingOutfitPerformanceAll
} from 'modules/campaigns/components/ccm/types/CcmCampaignReporting.type';

const ITEMS_PER_PAGE = 25;

type Props = {
  type: 'country' | 'outfit',
  data: Array<CcmCampaignReportingOutfitPerformanceAll>,
  page: number,
  sortingOptions: SortableTableSort,
  expandedRows: Array<number>,
  location: LocationWindow,
  onSetSortingOptions: (sortingOptions: SortableTableSort) => void,
  onSetExpandedRows: (rowId: number | null) => void,
  onSetPage: (page: number) => void
};

const CcmCampaignReportingTable: React.FC<Props> = ({
  type,
  data,
  page,
  sortingOptions,
  expandedRows,
  location,
  onSetSortingOptions,
  onSetExpandedRows,
  onSetPage
}): JSX.Element => {
  const pagination = dataProvider({
    page,
    limit: ITEMS_PER_PAGE,
    data: getSortedData(data, sortingOptions)
  });

  const columns = type === 'country'
    ? getPerformanceByCountryColumns()
    : getPerformanceByCountryOutfits();

  const handleRowStatus = (
    row: any // `any` is set by SortableTable
  ): string | null => {
    if (type === 'outfit' && !isEmpty(row.outfitDetails?.items)) {
      return 'dynamic';
    }

    return null;
  };

  const handleRowClick = (
    row: any // `any` is set by SortableTable
  ): void => {
    if (!isEmpty(row.outfitDetails?.items)) {
      onSetExpandedRows(row.id);
    }
  };

  const renderCollapsedContent = (
    row: any // `any` is set by SortableTable
  ): React.ReactNode => (
    <CcmCampaignReportingTableOutfitDetailsItems
      items={row.outfitDetails?.items ?? []}
    />
  );

  const containerClasses = classnames('ccm-campaign-reporting-table', {
    [`ccm-campaign-reporting-table--${type}`]: type
  });

  return (
    <div className={containerClasses}>
      <PageSection
        style="volume"
        padding="none"
        margin="none"
        border="none"
      >
        <SortableTable
          key={`ccm-campaign-reporting-table--${JSON.stringify(sortingOptions)}`}
          data={pagination.data}
          noScroll
          columns={columns}
          defaultSortingKey={sortingOptions.sortingKey}
          defaultSortingDirection={sortingOptions.ascending ? 'ascending' : 'descending'}
          layout="dense"
          expandedRows={expandedRows}
          isCollapsible={type === 'outfit' ? () => true : undefined}
          renderCollapsedContent={type === 'outfit' ? renderCollapsedContent : undefined}
          customClass="ccm-campaign-reporting-table__sortable"
          getRowStatus={handleRowStatus}
          onRowClicked={type === 'outfit' ? handleRowClick : undefined}
          onSortChanged={(updatedSortingOptions: SortableTableSort) => {
            onSetSortingOptions(updatedSortingOptions);
            onSetExpandedRows(null);
          }}
        />
      </PageSection>
      <Pagination
        key="ccm-campaign-reporting-table__pagination"
        location={location}
        meta={pagination.meta}
        links={pagination.links}
        onClick={(updatedPage: number) => onSetPage(updatedPage)}
      />
    </div>
  );
};

export default CcmCampaignReportingTable;
